import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function OrderSummary() {
  const { schoolId } = useParams();
  const [orders, setOrders] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/${schoolId}/orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [schoolId]);

  // The school-scoped admin options endpoint, so the printed header always
  // names the school currently selected in the URL, not just whichever
  // domain happens to be visited.
  const fetchSchoolName = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/${schoolId}/options`);
      setSchoolName(response.data.school?.name || '');
    } catch (err) {
      setSchoolName('');
    }
  }, [schoolId]);

  useEffect(() => {
    fetchOrders();
    fetchSchoolName();
  }, [fetchOrders, fetchSchoolName]);

  const ordersToPrint = orders
    .filter(order => order.paid === true)
    .sort((a, b) => {
      const gradeA = a.grade || '';
      const gradeB = b.grade || '';
      if (gradeA !== gradeB) {
        return gradeA.localeCompare(gradeB);
      }
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>Error: {error}</div>;

  // Group orders by grade
  const ordersByGrade = {};
  ordersToPrint.forEach(order => {
    const grade = order.grade || 'Unknown';
    if (!ordersByGrade[grade]) {
      ordersByGrade[grade] = [];
    }
    ordersByGrade[grade].push(order);
  });

  // Sort names within each grade
  Object.keys(ordersByGrade).forEach(grade => {
    ordersByGrade[grade].sort((a, b) => a.name.localeCompare(b.name));
  });

  const sortedGrades = Object.keys(ordersByGrade).sort();

  // Split grades into groups of 5 for multiple pages if needed
  const gradesPerPage = 5;
  const pages = [];
  for (let i = 0; i < sortedGrades.length; i += gradesPerPage) {
    pages.push(sortedGrades.slice(i, i + gradesPerPage));
  }

  return (
    <div>
      {/* Print Controls */}
      <div className="no-print" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div>
          <h3 style={{margin: '0 0 0.5rem 0'}}>Order Summary Preview</h3>
          <p style={{margin: 0, fontSize: '0.9rem', color: '#666'}}>
            {ordersToPrint.length} paid orders
          </p>
        </div>
        <button
          onClick={handlePrint}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          🖨️ Print Summary
        </button>
      </div>

      {/* Summary Content */}
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
        {ordersToPrint.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '50px',
            color: '#999',
            fontSize: '1.2rem'
          }}>
            No orders ready to print
          </div>
        ) : (
          pages.map((pageGrades, pageIndex) => (
            <div key={pageIndex} style={{
              pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'avoid',
              marginBottom: pageIndex < pages.length - 1 ? '2rem' : '0'
            }}>
              <h1 style={{
                textAlign: 'center',
                margin: '0 0 10px 0',
                fontSize: '14px'
              }}>
                {schoolName || 'Lunchday'} - Order Summary
              </h1>
              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-start'
              }}>
                {pageGrades.map(grade => (
                  <div key={grade} style={{
                    flex: 1,
                    border: '2px solid #000',
                    minWidth: 0
                  }}>
                    <div style={{
                      backgroundColor: '#000',
                      color: '#fff',
                      padding: '6px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      borderBottom: '2px solid #000',
                      WebkitPrintColorAdjust: 'exact',
                      printColorAdjust: 'exact'
                    }}>
                      Grade: {grade} ({ordersByGrade[grade].length} {ordersByGrade[grade].length === 1 ? 'order' : 'orders'})
                    </div>
                    {ordersByGrade[grade].map((order, idx) => (
                      <div key={idx} style={{
                        padding: '3px 6px',
                        borderBottom: '1px solid #ccc',
                        fontSize: '9px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {order.name}{order.teacher && ` (${order.teacher})`}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            margin: 0.25in;
            size: letter portrait;
          }
          body {
            padding: 0;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}

export default OrderSummary;
